import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Testimonials from '../components/Testimonials';
import ScrollAnimate from '../components/ScrollAnimate';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import ContactForm from '../components/ContactForm';
import { beforeAfterMock } from '../data';

export default function Home() {
  const [beforeAfterItems, setBeforeAfterItems] = useState(beforeAfterMock.slice(0, 3));
  const [loadingBeforeAfter, setLoadingBeforeAfter] = useState(true);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const togglePlay = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (videoRef.current) {
      if (videoRef.current.paused) {
        // Play with sound enabled (unmuted)
        videoRef.current.muted = false;
        setIsMuted(false);
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch((err) => {
              console.warn("Afspilningsfejl med lyd, prøver mute fallback:", err);
              // Fallback hvis browser blokerer autoplay med lyd
              if (videoRef.current) {
                videoRef.current.muted = true;
                setIsMuted(true);
                videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
              }
            });
        }
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (videoRef.current) {
      const nextMuted = !videoRef.current.muted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
    }
  };

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/before_after')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
          setBeforeAfterItems(sorted.slice(0, 3)); // Kun de første 3 på forsiden
        }
        setLoadingBeforeAfter(false);
      })
      .catch(err => {
        console.error('Fejl ved hentning af før/efter billeder:', err);
        setLoadingBeforeAfter(false);
      });
  }, []);

  return (
    <>
      <SEO 
        title="Maler i Sønderborg, Als og Gråsten | Lokalt malerfirma | Få tilbud"
        description="Maler i Sønderborg, Als og Gråsten. Få hjælp til maling af vægge, lofter, facade, døre, vinduer, spartling og tapetsering. Ring i dag og få et tilbud."
      />

      {/* 1. Intro & Hero + 2. Video */}
      <section className="relative bg-[#faf9f6] pt-10 sm:pt-14 lg:pt-18 pb-12 sm:pb-16 lg:pb-24 overflow-hidden border-b border-stone-200/50">
        {/* Watermark Background */}
        <div className="absolute top-1/2 left-0 -translate-x-1/4 -translate-y-1/2 text-stone-200 pointer-events-none z-0">
          <i className="fa-solid fa-paintbrush text-[500px] lg:text-[600px] -rotate-12 opacity-5" />
        </div>
        
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Hero Content Block */}
            <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left">
              
              {/* Trustpilot Review Badge (Udelukkende 5-stjernede - ingen '20 års erfaring') */}
              <ScrollAnimate direction="up" delay={0} duration={0.6} className="w-full flex justify-center lg:justify-start">
                <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-1.5 lg:gap-2.5 mb-8 sm:mb-8 lg:mb-7">
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="flex h-5 w-5 sm:h-5.5 sm:w-5.5 items-center justify-center rounded-[2px] bg-[#00b67a] text-white text-[10px] sm:text-xs font-bold shadow-xs">
                        ★
                      </span>
                    ))}
                  </div>
                  <div className="text-center lg:text-left mt-0.5 lg:mt-0">
                    {/* Mobil: To linjer centreret under stjernerne */}
                    <div className="block lg:hidden">
                      <p className="text-xs sm:text-sm font-bold text-[#0b1e36] tracking-tight">
                        Udelukkende 5-stjernede
                      </p>
                      <p className="text-xs sm:text-sm text-[#536b88] tracking-tight">
                        anmeldelser på Trustpilot
                      </p>
                    </div>
                    {/* PC / Desktop: Én samlet linje */}
                    <div className="hidden lg:block">
                      <p className="text-sm font-bold text-[#0b1e36] tracking-tight">
                        Udelukkende 5-stjernede anmeldelser på Trustpilot
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollAnimate>

              {/* H1 Heading & Subtitle */}
              <ScrollAnimate direction="up" delay={0.1} duration={0.7} className="w-full">
                <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] xl:text-[3rem] font-extrabold text-[#0b1e36] tracking-tight leading-[1.15]">
                  Din maler i <br className="sm:hidden" />Sønderborg og omegn
                </h1>
                <p className="text-lg sm:text-xl font-medium text-[#24354d] mt-3 leading-snug">
                  Grundigt arbejde - flot resultat
                </p>
              </ScrollAnimate>

              {/* Accent Divider Bar */}
              <ScrollAnimate direction="up" delay={0.15} duration={0.7}>
                <div className="w-12 h-1 bg-[#ee6425] rounded-full mx-auto lg:mx-0 my-5 sm:my-6"></div>
              </ScrollAnimate>

              {/* Body Text & Quote: Mobil vs PC */}
              <ScrollAnimate direction="up" delay={0.2} duration={0.7} className="w-full">
                {/* MOBIL VISNING (lg:hidden) */}
                <div className="block lg:hidden text-center max-w-md mx-auto">
                  <div className="space-y-3.5 mb-6">
                    <p className="text-sm sm:text-base font-bold text-[#0b1e36] leading-snug">
                      Hos Syddansk Malerfirma får du godt håndværk, klare aftaler og personlig service.
                    </p>
                    <p className="text-sm leading-relaxed text-[#243851]">
                      Vi lægger vægt på, at du føler dig tryg<br />
                      gennem hele forløbet –<br />
                      fra første kontakt til sidste penselstrøg.
                    </p>
                  </div>

                  {/* Centreret citat */}
                  <blockquote className="pt-2 pb-2 space-y-1.5 text-center">
                    <p className="text-base sm:text-lg font-semibold italic text-[#0b1e36] leading-snug">
                      ”Ingen opgave er færdig,<br />før vi selv er stolte af resultatet”
                    </p>
                    <cite className="block text-xs uppercase font-bold tracking-widest text-[#ee6425] not-italic mt-1.5">
                      – Mette Frank
                    </cite>
                  </blockquote>
                </div>

                {/* PC / DESKTOP VISNING (hidden lg:block) - Opdelt i to afsnit (ikke sammensat) som i oplægget */}
                <div className="hidden lg:block text-left max-w-xl">
                  <div className="space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8">
                    <p className="font-semibold text-[#0b1e36] leading-snug">
                      Hos Syddansk Malerfirma får du godt håndværk, klare aftaler og personlig service.
                    </p>
                    <p className="text-slate-600">
                      Vi lægger vægt på, at du føler dig tryg gennem hele forløbet – fra første kontakt til sidste penselstrøg.
                    </p>
                  </div>

                  {/* Det oprindelige venstrejusterede citat med den orange streg til venstre */}
                  <blockquote className="border-l-4 border-[#ee6425] pl-4 py-1 space-y-1 my-6 sm:my-7 text-left">
                    <p className="text-base sm:text-lg lg:text-xl font-bold italic text-[#0b1e36]">
                      “Ingen opgave er færdig, før vi selv er stolte af resultatet.”
                    </p>
                    <footer className="text-xs font-bold tracking-wider uppercase text-[#ee6425] block mt-1">
                      — Mette Frank
                    </footer>
                  </blockquote>
                </div>
              </ScrollAnimate>

              {/* CTA Buttons */}
              <ScrollAnimate direction="up" delay={0.25} duration={0.7} className="w-full mt-4 sm:mt-6">
                <div className="flex flex-row items-center justify-center lg:justify-start gap-2.5 sm:gap-4 max-w-full mx-auto lg:mx-0">
                  <Link 
                    to="/kontakt" 
                    className="inline-flex items-center justify-center rounded-[14px] bg-[#ee6425] hover:bg-[#d95318] px-5 sm:px-7 py-3.5 text-xs sm:text-sm font-bold text-white shadow-md shadow-orange-500/20 transition-all active:scale-[0.98] uppercase tracking-wider text-center whitespace-nowrap"
                  >
                    Få et tilbud
                  </Link>
                  <a 
                    href="tel:24995995"
                    className="inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-[14px] bg-white border-2 border-[#ee6425] hover:bg-[#ee6425] hover:text-white px-5 sm:px-7 py-3 text-xs sm:text-sm font-bold text-[#ee6425] shadow-sm transition-all active:scale-[0.98] uppercase tracking-wider text-center whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-[10px] sm:text-xs" />
                    <span>Ring: 24 995 995</span>
                  </a>
                </div>
              </ScrollAnimate>

            </div>

            {/* Right Column: 2. Video Showcase (Mette ved vinduet) */}
            <div className="lg:col-span-5 w-full mt-4 lg:mt-0 flex justify-center lg:justify-start lg:pl-2">
              <ScrollAnimate direction="left" delay={0.3} duration={0.9} className="w-full max-w-[320px] sm:max-w-[360px] lg:max-w-[430px] xl:max-w-[460px] mx-auto lg:mx-0">
                <div 
                  className="relative rounded-3xl overflow-hidden shadow-xl border border-stone-200/80 aspect-[9/16] max-h-[480px] sm:max-h-[580px] lg:max-h-[680px] xl:max-h-[720px] w-full bg-slate-900 group cursor-pointer select-none" 
                  onClick={togglePlay}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      togglePlay(e);
                    }
                  }}
                  aria-label={isPlaying ? "Sæt video på pause" : "Afspil video"}
                >
                  <video 
                    ref={videoRef}
                    src="https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6879-1.mp4"
                    poster="https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6879-1.jpg"
                    loop 
                    playsInline 
                    preload="auto"
                    onPlay={() => {
                      setIsPlaying(true);
                      if (videoRef.current) setIsMuted(videoRef.current.muted);
                    }}
                    onPause={() => setIsPlaying(false)}
                    className="w-full h-full object-cover absolute inset-0"
                  />

                  {/* Lydstyringsknap når videoen afspilles */}
                  {isPlaying && (
                    <button
                      type="button"
                      onClick={toggleMute}
                      aria-label={isMuted ? "Slå lyd til" : "Slå lyd fra"}
                      className="absolute top-4 right-4 z-30 size-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm border border-white/20 shadow-lg transition-transform active:scale-95"
                    >
                      <i className={`fa-solid ${isMuted ? 'fa-volume-xmark' : 'fa-volume-high'} text-sm`} />
                    </button>
                  )}

                  {/* Big Center Play Button (Visible when paused) */}
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-[0.5px] transition-all z-20 pointer-events-none">
                      <div 
                        aria-hidden="true"
                        className="size-16 sm:size-20 rounded-full bg-[#ee6425] hover:bg-[#d95318] text-white flex items-center justify-center shadow-2xl border-3 border-white transition-transform transform group-hover:scale-110 active:scale-95"
                      >
                        <i className="fa-solid fa-play text-xl sm:text-2xl ml-1" />
                      </div>
                    </div>
                  )}
                </div>
              </ScrollAnimate>
            </div>

          </div>
        </div>
      </section>

      {/* 3. Derfor vælger kunderne Syddansk Malerfirma (Kompakt på mobil) */}
      <section className="py-12 sm:py-16 lg:py-20 bg-stone-50 border-b border-stone-200/60">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-center">
            
            {/* Left text side */}
            <div className="lg:col-span-5">
              <ScrollAnimate direction="right" delay={0.1} duration={0.8}>
                <span className="text-[#ee6425] font-bold tracking-wider uppercase text-xs sm:text-sm mb-1.5 sm:mb-2.5 block">
                  Hvorfor vælge os
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-800 tracking-tight leading-[1.15]">
                  Derfor vælger kunderne Syddansk Malerfirma:
                </h2>
                <div className="w-12 h-1 bg-[#ee6425] rounded-full mt-3 sm:mt-4"></div>
              </ScrollAnimate>
            </div>

            {/* Right grid of checkmarks - Kompakt på mobil med ca. 20-25% mindre luft */}
            <div className="lg:col-span-7">
              <ScrollAnimate direction="left" delay={0.2} duration={0.8}>
                <ul className="divide-y divide-stone-200/80">
                  {[
                    "Grundigt forarbejde der sikrer et flot og holdbart resultat",
                    "Professionelt håndværk med fokus på kvalitet",
                    "Klare aftaler og god kommunikation",
                    "Kvalitetsprodukter med fokus på miljø og indeklima",
                    "Gratis og uforpligtende tilbud"
                  ].map((point, index) => (
                    <li key={index} className="flex items-center gap-3.5 py-3 sm:py-3.5 first:pt-0 last:pb-0">
                      <span className="flex size-5.5 sm:size-6 items-center justify-center rounded-full bg-[#00b67a] text-white shrink-0 shadow-2xs">
                        <i className="fa-solid fa-check text-[10px] sm:text-xs" />
                      </span>
                      <span className="text-slate-800 font-bold text-sm sm:text-base lg:text-[16px] leading-snug">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </ScrollAnimate>
            </div>

          </div>
        </div>
      </section>

      {/* 4. Før og efter (Se forskellen - placeret FØR anmeldelserne) */}
      <section className="py-14 sm:py-18 lg:py-22 bg-white border-b border-stone-200/60">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <ScrollAnimate direction="up" delay={0.1} duration={0.8} className="text-center mb-8 sm:mb-12 max-w-3xl mx-auto">
            <span className="text-[#ee6425] font-bold tracking-wider uppercase text-xs sm:text-sm mb-1.5 sm:mb-2 block">Se forskellen</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-800 tracking-tight leading-[1.15] mb-2.5 sm:mb-3">
              Før og efter:
            </h2>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Et veludført malerarbejde handler om detaljerne. Vi udfører alle opgaver med stor omhu og leverer et resultat, der både ser flot ud og holder i mange år.
            </p>
            <div className="w-12 h-1 bg-[#ee6425] rounded-full mx-auto mt-3 sm:mt-4"></div>
          </ScrollAnimate>

          {loadingBeforeAfter ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-stone-500 text-xs sm:text-sm font-medium">Henter billeder...</p>
            </div>
          ) : beforeAfterItems.length === 0 ? (
            <p className="text-stone-500 text-sm sm:text-base text-center">Ingen før/efter billeder tilgængelige lige nu.</p>
          ) : (
            <div className="relative w-full flex flex-col items-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 w-full pb-8 sm:pb-10">
                {beforeAfterItems.map((item, index) => (
                  <ScrollAnimate 
                    key={item.id} 
                    direction="up" 
                    delay={index * 0.1} 
                    duration={0.8}
                  >
                    <BeforeAfterSlider 
                      beforeImage={item.before_image?.[0]} 
                      afterImage={item.after_image?.[0]} 
                      title={item.title?.da}
                      position={item.title?.da?.toLowerCase().includes('loft') ? 'top' : 'center'}
                    />
                  </ScrollAnimate>
                ))}
              </div>

              <div className="relative z-10 mt-1 sm:mt-3">
                <Link 
                  to="/foer-efter" 
                  className="inline-flex items-center gap-2 bg-white border-2 border-[#ee6425] text-[#ee6425] hover:bg-[#ee6425] hover:text-white font-bold py-2.5 sm:py-3 px-6 sm:px-7 rounded-full shadow-xs transition-all duration-200 text-xs sm:text-sm uppercase tracking-wide transform hover:-translate-y-0.5 active:scale-95"
                >
                  <span>Se galleri</span>
                  <i className="fa-solid fa-chevron-right text-[10px] sm:text-xs" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 5. Det siger vores kunder / Kundeanmeldelser (uden gentaget Trustpilot-stjernelinje) */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FAF9F6] border-b border-stone-200/60 overflow-hidden">
        <Testimonials />
      </section>

      {/* 6. Vores ydelser (Professionelt malerarbejde til alle behov - mere kompakt på mobil) */}
      <section className="py-14 sm:py-18 lg:py-22 bg-[#0F352E] text-white border-b border-teal-950">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          
          {/* Centered Header */}
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <ScrollAnimate direction="up" delay={0.1} duration={0.8}>
              <span className="text-orange-400 font-bold tracking-wider uppercase text-xs sm:text-sm mb-1.5 sm:mb-2 block">Vores ydelser</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-[1.15] mb-2 sm:mb-3">
                Professionelt malerarbejde til alle behov
              </h2>
              <p className="text-teal-100 text-sm sm:text-base mb-4 sm:mb-5 leading-relaxed">
                Vi udfører blandt andet følgende opgaver med stor præcision og kvalitet:
              </p>
              <div className="w-12 h-1 bg-orange-500 rounded-full mx-auto"></div>
            </ScrollAnimate>
          </div>

          {/* Services Grid (Kompakt på mobil) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { 
                title: "Maling af vægge og lofter", 
                desc: "Vi maler vægge og lofter med stor præcision og dækkeevne for et ensartet resultat.",
                href: "/maling-af-vaegge",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6701.jpeg"
              },
              { 
                title: "Spartling", 
                desc: "Fuldspartling og pletspartling, der sikrer det helt perfekte underlag til maling eller filt.",
                href: "/spartling",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-1547_1.jpeg"
              },
              { 
                title: "Opsætning af filt og tapet", 
                desc: "Professionel opsætning af glasfilt, miljøvæv eller mønstret tapet efter dine ønsker.",
                href: "/tapetsering",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5499_7a55de1e.jpeg"
              },
              { 
                title: "Facademaling", 
                desc: "Slidstærkt og vejrbestandigt maling af facader, der beskytter dit byggeri i mange år.",
                href: "/maling-af-facade",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5165_3.jpg"
              },
              { 
                title: "Maling af vinduer, døre og karme", 
                desc: "Præcisionsarbejde på træværk, døre, vinduesrammer og karme med slidstærk lak.",
                href: "/maling-af-vinduer",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-7173.jpeg"
              },
              { 
                title: "Erhvervsmaling", 
                desc: "Malerarbejde til kontorer, butikker og erhvervsejendomme planlagt efter jeres drift.",
                href: "/erhvervsmaler",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6956.jpeg"
              },
              { 
                title: "Forsikringsskader", 
                desc: "Hurtig og professionel udbedring af malerarbejde efter vandskader, brand eller sætningsskader.",
                href: "/forsikringsskader",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5298.jpg"
              },
              { 
                title: "Maling for boligforeninger", 
                desc: "Istandsættelse ved fraflytning, maling af opgange og løbende vedligeholdelse.",
                href: "/flyttelejligheder",
                image: "https://assets.auxodev.dk/cdn-cgi/image/width=120,height=120,fit=cover,quality=85,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5510.jpeg"
              }
            ].map((service, index) => (
              <ScrollAnimate 
                key={index} 
                direction="up" 
                delay={index * 0.04} 
                duration={0.7}
                className="group flex flex-col justify-between py-3 sm:py-4 border-b border-teal-800/60 transition-all duration-300"
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="size-10 sm:size-11 shrink-0 rounded-lg bg-teal-950 overflow-hidden border border-teal-800 flex items-center justify-center">
                      <img 
                        src={service.image} 
                        alt={service.title} 
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-orange-400 transition-colors leading-snug">{service.title}</h3>
                  </div>
                  <p className="text-teal-100/80 text-xs sm:text-sm leading-relaxed mb-2.5">{service.desc}</p>
                </div>
                <Link 
                  to={service.href} 
                  className="inline-flex items-center gap-1.5 text-orange-400 font-bold hover:text-white transition-colors text-xs uppercase tracking-wider mt-auto"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[10px] transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </ScrollAnimate>
            ))}
          </div>

        </div>
      </section>

      {/* 7. Sådan foregår samarbejdet (Mindre lodret luft på mobil) */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white border-b border-stone-200/60">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <ScrollAnimate direction="up" delay={0.1} duration={0.8} className="text-center mb-8 sm:mb-12">
            <span className="text-[#ee6425] font-bold tracking-wider uppercase text-xs sm:text-sm mb-1.5 sm:mb-2 block">Processen</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-800 tracking-tight leading-[1.15] mb-2 sm:mb-3">
              Sådan foregår samarbejdet
            </h2>
            <div className="w-12 h-1 bg-[#ee6425] rounded-full mx-auto mt-2"></div>
          </ScrollAnimate>

          {/* Process Timeline Grid - Kompakt på mobil */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 relative">
            {[
              {
                step: "1",
                title: "Kontakt os",
                desc: "Fortæl os om din opgave"
              },
              {
                step: "2",
                title: "Besigtigelse",
                desc: "Vi gennemgår opgaven og rådgiver dig om den bedste løsning"
              },
              {
                step: "3",
                title: "Tilbud",
                desc: "Du modtager et gratis og uforpligtende tilbud"
              },
              {
                step: "4",
                title: "Udførelse",
                desc: "Vi udfører arbejdet som aftalt - med fokus på kvaliteten og detaljerne"
              },
              {
                step: "5",
                title: "Aflevering",
                desc: "Vi afslutter først, når resultatet lever op til både dine og vores forventninger"
              }
            ].map((stepItem, index) => (
              <ScrollAnimate 
                key={index} 
                direction="up" 
                delay={index * 0.08} 
                duration={0.7} 
                className="relative flex flex-col items-center text-center p-3 sm:p-4 bg-stone-50/70 md:bg-transparent rounded-2xl md:rounded-none"
              >
                <div className="size-11 sm:size-13 md:size-14 rounded-full bg-[#1a6650] text-white flex items-center justify-center text-base sm:text-lg md:text-xl font-black shadow-md mb-2.5 sm:mb-4 border-2 sm:border-3 border-teal-50 relative z-10">
                  {stepItem.step}
                </div>
                <h3 className="text-base sm:text-lg font-extrabold text-slate-800 mb-1 leading-snug">{stepItem.title}</h3>
                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed max-w-[200px] md:max-w-none">
                  {stepItem.desc}
                </p>
              </ScrollAnimate>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Mød Mette – personen bag Syddansk Malerfirma (Originalfoto i fuld bredde uden zoom/sorte kanter) */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FAF9F6] border-b border-stone-200/60">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12">
          
          {/* Header */}
          <ScrollAnimate direction="up" delay={0.1} duration={0.8} className="space-y-1.5 sm:space-y-2 mb-6 sm:mb-8">
            <span className="text-xs font-bold uppercase tracking-wider text-[#ee6425]">
              Historien bag
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#0b1c36] leading-tight">
              Mød Mette – personen bag<br className="hidden sm:inline" /> Syddansk Malerfirma
            </h2>
            <div className="w-12 h-1 bg-[#ee6425] rounded-full mt-2 sm:mt-3"></div>
          </ScrollAnimate>

          {/* Full Width Image of Mette & the company car */}
          <ScrollAnimate direction="up" delay={0.15} duration={0.8} className="mb-6 sm:mb-8">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-md border border-stone-200/80 bg-stone-100 w-full">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6842-2.jpeg" 
                alt="Mette Frank foran firmabilen - Indehaver af Syddansk Malerfirma" 
                className="w-full h-auto object-cover object-top"
                loading="lazy"
              />
            </div>
          </ScrollAnimate>

          {/* Content Block below image using full reading width */}
          <div className="space-y-5 sm:space-y-6">
            
            {/* Story Paragraphs */}
            <ScrollAnimate direction="up" delay={0.2} duration={0.8} className="space-y-3 sm:space-y-4 text-stone-700 text-sm sm:text-base md:text-lg leading-relaxed">
              <p>
                Jeg stiftede Syddansk Malerfirma i 2021 med ønsket om at skabe et malerfirma, hvor kvalitet, ordentlighed og god service altid kommer først<br className="hidden sm:inline" />
                - Det er de værdier, vi arbejder efter hver eneste dag.
              </p>
              <p>
                Det bedste ved malerfaget er at skabe synlige forandringer. Der er noget særligt ved at se et rum eller en bygning få nyt liv gennem godt håndværk.
              </p>
            </ScrollAnimate>

            {/* Editorial Quote Block - matcher citatet højere oppe */}
            <ScrollAnimate direction="up" delay={0.25} duration={0.8}>
              <blockquote className="border-l-4 border-[#ee6425] pl-4 py-1 space-y-1 my-5 sm:my-6 text-left">
                <p className="text-base sm:text-lg lg:text-xl font-bold italic text-[#0b1e36]">
                  “Vores mål er altid at levere et flot og holdbart resultat, der lever op til både dine og vores forventninger.”
                </p>
                <footer className="text-xs font-bold tracking-wider uppercase text-[#ee6425] block mt-1">
                  — Mette Frank
                </footer>
              </blockquote>
            </ScrollAnimate>

            {/* Silver Medal Badge Card (Lille & kompakt boks med mørkegrøn baggrund) */}
            <ScrollAnimate direction="up" delay={0.3} duration={0.8} className="w-full">
              <div className="flex items-center gap-7 sm:gap-9 mt-6 w-full border-y border-stone-300 py-5 sm:py-6">
                <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 flex items-center justify-center ml-[50px]">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=95,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/imgi-2-medalje-115-rez.png" 
                    alt="Sølvmedalje for vel udført svendeprøve i malerfaget" 
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="w-[1.5px] self-stretch my-2 bg-stone-300 shrink-0 mx-2 sm:mx-4" aria-hidden="true" />
                <div className="space-y-1 sm:space-y-1.5">
                  <h3 className="text-lg sm:text-xl font-bold text-[#0F352E] leading-snug">
                    Sølvmedalje
                  </h3>
                  <p className="text-sm sm:text-base text-stone-600 font-medium leading-normal">
                    For veludført svendeprøve<br />i malerfaget.
                  </p>
                </div>
              </div>
            </ScrollAnimate>

          </div>
        </div>
      </section>

      {/* 9. Geografi (Din lokale maler i Sønderjylland - 2 kolonner, kompakt på mobil) */}
      <section className="py-12 sm:py-16 lg:py-20 bg-stone-50 border-b border-stone-200/60">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left text */}
            <div className="lg:col-span-5">
              <ScrollAnimate direction="right" delay={0.1} duration={0.8}>
                <span className="text-[#ee6425] font-extrabold tracking-widest uppercase text-xs sm:text-sm mb-1.5 sm:mb-2 block">
                  Geografi
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#0d1b2a] tracking-tight leading-[1.15] mb-2 sm:mb-3">
                  Din lokale maler i<br className="hidden sm:inline" /> Sønderjylland
                </h2>
                <div className="w-12 h-1 bg-[#ee6425] rounded-full mt-2 mb-4"></div>
                <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                  Vi udfører malerarbejde for private, virksomheder og boligforeninger på hele Als og i det sydlige Sønderjylland.
                </p>
              </ScrollAnimate>
            </div>

            {/* Right grid of cities (2 columns as requested, compact vertical padding on mobile) */}
            <div className="lg:col-span-7">
              <ScrollAnimate direction="left" delay={0.2} duration={0.8}>
                <p className="font-bold text-[#0d1b2a] text-sm sm:text-base mb-2.5 sm:mb-3.5">
                  Vi kører blandt andet i:
                </p>
                <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-0.5 sm:gap-y-1">
                  {[
                    { name: "Sønderborg", slug: null },
                    { name: "Gråsten", slug: null },
                    { name: "Nordborg", slug: "/maler-i-nordborg" },
                    { name: "Aabenraa", slug: "/maler-i-aabenraa" },
                    { name: "Broager", slug: null },
                    { name: "Dybbøl", slug: "/maler-i-dybboel" },
                    { name: "Augustenborg", slug: "/maler-i-augustenborg" },
                    { name: "Guderup", slug: "/maler-i-guderup" },
                    { name: "Fynshav", slug: null },
                    { name: "Rinkenæs", slug: null },
                    { name: "Kruså", slug: null },
                    { name: "Padborg", slug: "/maler-i-padborg" }
                  ].map((city, index) => {
                    const content = (
                      <>
                        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                          <span className="text-[#ee6425] shrink-0 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current" viewBox="0 0 24 24">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
                            </svg>
                          </span>
                          <span className="font-semibold text-xs sm:text-sm md:text-base text-[#0d1b2a] group-hover:text-[#ee6425] transition-colors truncate">
                            {city.name}
                          </span>
                        </div>
                        {city.slug && (
                          <span className="ml-1 text-[10px] sm:text-xs text-stone-400 group-hover:text-[#ee6425] transition-colors shrink-0">
                            <i className="fa-solid fa-chevron-right" />
                          </span>
                        )}
                      </>
                    );

                    return city.slug ? (
                      <Link
                        key={index}
                        to={city.slug}
                        className="group flex items-center justify-between py-2 sm:py-2.5 border-b border-stone-200/60 hover:border-[#ee6425] transition-all"
                      >
                        {content}
                      </Link>
                    ) : (
                      <div
                        key={index}
                        className="group flex items-center justify-between py-2 sm:py-2.5 border-b border-stone-200/60"
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>

                {/* Contact callout box after cities */}
                <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-stone-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6">
                  <div className="flex items-center gap-3.5 sm:gap-4">
                    {/* Speech bubble badge with tinted peach background */}
                    <div className="flex size-12 sm:size-14 shrink-0 items-center justify-center rounded-full bg-[#FEECE3]">
                      <svg className="size-6 text-[#ee6425]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-base font-bold text-[#0d1b2a]">
                        Kan du ikke finde din by?
                      </h4>
                      <p className="text-xs sm:text-sm text-stone-600 mt-0.5 leading-relaxed">
                        Vi hjælper også kunder i nærliggende områder.
                      </p>
                    </div>
                  </div>
                  <Link 
                    to="/kontakt"
                    className="inline-flex items-center justify-center gap-2 bg-[#ee6425] hover:bg-[#d95318] text-white font-bold py-3.5 px-6 rounded-full text-xs sm:text-sm uppercase tracking-wider transition-all shrink-0 shadow-md shadow-orange-500/20 active:scale-95 text-center w-full sm:w-auto"
                  >
                    <span>Skriv til os her</span>
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              </ScrollAnimate>
            </div>

          </div>
        </div>
      </section>

      {/* 10. Bæredygtig kvalitet / Miljø (Kompakt støtteargument) */}
      <section className="py-10 sm:py-12 lg:py-14 bg-stone-100/70 border-b border-stone-200/60">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <ScrollAnimate direction="up" delay={0.1} duration={0.6}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-8">
              
              {/* Text side */}
              <div className="flex-1 space-y-1.5 sm:space-y-2 text-center md:text-left">
                <div className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wider">
                  <i className="fa-solid fa-leaf text-emerald-600 text-sm" />
                  <span>Bæredygtig kvalitet</span>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-800 tracking-tight">
                  Miljøvenlige produkter – uden at gå på kompromis med kvaliteten
                </h3>
                <p className="text-stone-600 text-xs sm:text-sm md:text-base leading-relaxed max-w-3xl">
                  Vi anvender kvalitetsprodukter fra blandt andet Flügger og Beck & Jørgensen. Mange af produkterne er miljømærkede og bidrager til et sundere indeklima uden at gå på kompromis med holdbarhed og finish.
                </p>
              </div>

              {/* Badges side-by-side */}
              <div className="flex items-center gap-4 sm:gap-6 shrink-0 justify-center">
                <div className="w-20 sm:w-24 h-12 sm:h-14 flex items-center justify-center">
                  <img 
                    src="https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/c8e6a2f254d6172d01c243bbfbebde944fc7e277-90x90.svg" 
                    alt="Svanemærket" 
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="w-20 sm:w-24 h-12 sm:h-14 flex items-center justify-center">
                  <img 
                    src="https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/eu.svg" 
                    alt="EU Ecolabel" 
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>

            </div>
          </ScrollAnimate>
        </div>
      </section>

      {/* 11. FAQ (Ofte stillede spørgsmål) */}
      <section className="py-12 sm:py-16 lg:py-20 bg-stone-50 border-b border-stone-200/60">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <ScrollAnimate direction="up" delay={0.1} duration={0.8} className="text-center mb-8 sm:mb-12">
            <span className="text-[#ee6425] font-bold tracking-wider uppercase text-xs sm:text-sm mb-1.5 sm:mb-2 block">FAQ</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-800 tracking-tight leading-[1.15] mb-2 sm:mb-3">
              Ofte stillede spørgsmål
            </h2>
            <div className="w-12 h-1 bg-[#ee6425] rounded-full mx-auto mt-2"></div>
          </ScrollAnimate>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 sm:gap-y-6">
            {[
              {
                q: "Tilbyder I gratis tilbud?",
                a: "Ja, alle tilbud er gratis og uforpligtende."
              },
              {
                q: "Hvilke områder kører I i?",
                a: "Vi udfører malerarbejde på hele Als og i det sydlige Sønderjylland."
              },
              {
                q: "Udfører I både små og store opgaver?",
                a: "Ja. Vi hjælper med alt fra enkelte rum til større boliger, erhvervsejendomme og boligforeninger."
              },
              {
                q: "Bruger I kvalitetsprodukter?",
                a: "Ja, vi anvender kvalitetsprodukter og vælger løsninger der passer til den enkelte opgave."
              }
            ].map((faq, index) => (
              <ScrollAnimate 
                key={index} 
                direction="up" 
                delay={index * 0.05} 
                duration={0.7}
                className="py-2.5 sm:py-3 border-b border-stone-200/80"
              >
                <h3 className="text-base sm:text-lg font-extrabold text-slate-800 mb-1.5 flex items-start gap-2">
                  <span className="text-[#ee6425] mt-0.5 shrink-0"><i className="fa-solid fa-circle-question text-sm sm:text-base" /></span>
                  <span>{faq.q}</span>
                </h3>
                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed pl-5 sm:pl-6">
                  {faq.a}
                </p>
              </ScrollAnimate>
            ))}
          </div>
        </div>
      </section>

      {/* 12. Kontakt & Formular */}
      <section className="relative py-14 sm:py-20 lg:py-24 bg-[#F8F7F4] border-b-8 border-teal-900 overflow-hidden" id="hjaelp-sektion">
        {/* Decorative background elements */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-teal-900/5 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto">
            
            {/* Kontaktsektion & Formular (Billedet fjernet iht. kundeønske) */}
            <div>
              <ScrollAnimate direction="up" delay={0.1} duration={0.8}>
                <div className="text-center sm:text-left">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#ee6425] mb-2 sm:mb-3 block">
                    Kontakt os
                  </span>
                  <h2 className="text-2xl sm:text-3xl lg:text-[2.5rem] font-black text-slate-800 tracking-tight leading-[1.15] mb-3 sm:mb-4">
                    Skal vi hjælpe med dit næste malerprojekt?
                  </h2>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-6">
                    Vi står klar til at rådgive dig og give et uforpligtende tilbud på din opgave. Vi leverer et resultat i topkvalitet.
                  </p>
                </div>
                
                {/* 2 Knapper (den ene udfyldt, den anden med en ring om - ligesom øverst) */}
                <div className="flex flex-row items-center justify-center sm:justify-start gap-2.5 sm:gap-4 max-w-full mb-6 sm:mb-8">
                  <a 
                    href="#kontaktformular-forside"
                    className="inline-flex items-center justify-center rounded-[14px] bg-[#ee6425] hover:bg-[#d95318] px-5 sm:px-7 py-3.5 text-xs sm:text-sm font-bold text-white shadow-md shadow-orange-500/20 transition-all active:scale-[0.98] uppercase tracking-wider text-center whitespace-nowrap"
                  >
                    Få et tilbud
                  </a>
                  <a 
                    href="tel:24995995" 
                    className="inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-[14px] bg-white border-2 border-[#ee6425] hover:bg-[#ee6425] hover:text-white px-5 sm:px-7 py-3 text-xs sm:text-sm font-bold text-[#ee6425] shadow-sm transition-all active:scale-[0.98] uppercase tracking-wider text-center whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-[10px] sm:text-xs" />
                    <span>Ring: 24 995 995</span>
                  </a>
                </div>

                <div id="kontaktformular-forside" className="mt-6 pt-2 w-full">
                  <p className="text-stone-600 text-xs sm:text-sm mb-4 flex items-center gap-2 font-semibold justify-center sm:justify-start">
                    <i className="fa-solid fa-clock text-orange-500 text-sm sm:text-base shrink-0" />
                    <span>Udfyld kontaktformularen, så vender vi tilbage hurtigst muligt:</span>
                  </p>
                  
                  {/* Kontaktformular */}
                  <div className="bg-white p-5 sm:p-8 rounded-3xl shadow-lg border border-stone-200/80 w-full overflow-hidden">
                    <ContactForm />
                  </div>
                </div>
              </ScrollAnimate>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
